import React, { useState } from 'react';
import { Plus, Pencil, Trash2, FolderOpen, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InterviewFolder } from '@/hooks/interview/useInterviewFolders';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ConfirmationDialog from './ConfirmationDialog';

interface FolderTabsProps {
  folders: InterviewFolder[];
  activeFolderId: string | null; // null = "Alle"
  onSelectFolder: (folderId: string | null) => void;
  onCreateFolder: (name: string) => void;
  onRenameFolder: (id: string, name: string) => void;
  onDeleteFolder: (id: string) => void;
  folderCounts: Record<string, number>;
  totalCount: number;
}

const FolderTabs: React.FC<FolderTabsProps> = ({
  folders,
  activeFolderId,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  folderCounts,
  totalCount,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleCreate = () => {
    const trimmed = newFolderName.trim();
    if (trimmed) {
      onCreateFolder(trimmed);
      setNewFolderName('');
      setIsCreating(false);
    }
  };

  const handleRename = (id: string) => {
    const trimmed = editingName.trim();
    if (trimmed) {
      onRenameFolder(id, trimmed);
      setEditingId(null);
    }
  };

  const tabBase = "px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap flex items-center gap-1.5";
  const tabActive = "bg-primary text-primary-foreground font-medium";
  const tabInactive = "bg-muted/50 text-muted-foreground hover:bg-muted";

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {/* Alle tab */}
      <button
        className={`${tabBase} ${activeFolderId === null ? tabActive : tabInactive}`}
        onClick={() => onSelectFolder(null)}
      >
        Alle ({totalCount})
      </button>

      {/* Folder tabs */}
      {folders.map(folder => (
        <div key={folder.id} className="flex items-center">
          {editingId === folder.id ? (
            <div className="flex items-center gap-1">
              <Input
                value={editingName}
                onChange={e => setEditingName(e.target.value)}
                className="h-7 w-32 text-sm"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') handleRename(folder.id);
                  if (e.key === 'Escape') setEditingId(null);
                }}
              />
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleRename(folder.id)}>
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingId(null)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`${tabBase} ${activeFolderId === folder.id ? tabActive : tabInactive}`}
                  onClick={(e) => {
                    // Left click selects, right click opens menu
                    e.preventDefault();
                    onSelectFolder(folder.id);
                  }}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <FolderOpen className="h-3.5 w-3.5" />
                  {folder.name} ({folderCounts[folder.id] || 0})
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => onSelectFolder(folder.id)}>
                  <FolderOpen className="h-4 w-4 mr-2" /> Åpne
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setEditingId(folder.id); setEditingName(folder.name); }}>
                  <Pencil className="h-4 w-4 mr-2" /> Gi nytt navn
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => setDeleteConfirmId(folder.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Slett mappe
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ))}

      {/* Create new folder */}
      {isCreating ? (
        <div className="flex items-center gap-1">
          <Input
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            placeholder="Mappenavn..."
            className="h-7 w-32 text-sm"
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter') handleCreate();
              if (e.key === 'Escape') { setIsCreating(false); setNewFolderName(''); }
            }}
          />
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleCreate}>
            <Check className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setIsCreating(false); setNewFolderName(''); }}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={() => setIsCreating(true)}
        >
          <Plus className="h-3.5 w-3.5" /> Ny mappe
        </Button>
      )}

      <ConfirmationDialog
        isOpen={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={() => {
          if (deleteConfirmId) {
            onDeleteFolder(deleteConfirmId);
            if (activeFolderId === deleteConfirmId) onSelectFolder(null);
            setDeleteConfirmId(null);
          }
        }}
        title="Slett mappe"
        description="Er du sikker på at du vil slette denne mappen? Artiklene i mappen vil ikke bli slettet."
        confirmText="Slett"
        cancelText="Avbryt"
      />
    </div>
  );
};

export default FolderTabs;
